import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSocket } from '../../contexts/SocketContext';
import { useToast } from '../../components/Toast';
import TabBar from '../../components/TabBar';
import api from '../../utils/api';
// eslint-disable-next-line no-unused-vars
import { Database, Activity, Download, Wallet, AlertTriangle, QrCode, Search, Camera, ChevronDown, ChevronUp, Copy, Check, PhoneCall, CheckCircle2, Bell, FileSpreadsheet, ClipboardList, TrendingUp, Video } from 'lucide-react';
import AdminNotificationBell from '../../components/AdminNotificationBell';
import ForceCompleteModal from '../../components/ForceCompleteModal';
import BizSwitch from '../../components/BizSwitch';
import { useBizMode } from '../../utils/bizMode';
import { copyToClipboard } from '../../utils/clipboard';
import { buildXlsx, shareFile } from '../../utils/share';
import { filenameTimestamp } from '../../utils/datetime';
import { useBeijingToday } from '../../utils/useBeijingToday';
import { Package, PackageCheck, ShieldAlert, ScanLine } from 'lucide-react';

export default function AdminDashboard() {
  const { user, isAdmin, isAgent, canSendPush } = useAuth();
  const { subscribe } = useSocket();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [agentOverview, setAgentOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null); // 今日接量排行展开行（显示号码）
  const [rankingPage, setRankingPage] = useState(0); // [需求#10] 今日接量排行分页，20/页
  const [copiedAllId, setCopiedAllId] = useState(null); // 某行"一键复制全部"反馈
  const [forceModalUser, setForceModalUser] = useState(null); // 代完成弹层目标用户
  const [bizMode] = useBizMode();                       // 排五 / 证券（与前台、结算、获客素材共用同一个全局选择）
  const [puzzle, setPuzzle] = useState(null);             // /puzzle/admin/overview：证券业务汇总
  const [wdPending, setWdPending] = useState(0);          // 待审批提现（两个业务共用）
  const [vrPending, setVrPending] = useState(0);          // 待审查视频（两个业务共用）
  const today = useBeijingToday();                        // 页面开着跨天时，靠这个触发重新拉“今日”相关的统计（不然只能等业务事件）

  const copyText = useCallback(async (text, label = '已复制') => {
    await copyToClipboard(text);
    showToast(label, 'success');
  }, [showToast]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await api.get('/admin/stats');
      setStats(res.data);
      if (isAgent) {
        const ov = await api.get('/settlement/agent-overview').catch(() => ({ data: null }));
        if (ov.data) setAgentOverview(ov.data);
      }
    } catch (err) {
      if (err.response?.status !== 401) showToast('加载失败', 'error');
    } finally { setLoading(false); }
  }, [showToast, isAgent]);

  const handleCopyUserAll = useCallback((u) => {
    const text = (u.numbers || []).filter(Boolean).join('\n');
    if (!text) return;
    copyText(text, `已复制 ${u.name || u.phone} 的 ${u.numbers.length} 个号码`);
    setCopiedAllId(u.id);
    setTimeout(() => setCopiedAllId(null), 1500);
  }, [copyText]);

  // 排行展开行：把该用户今日号码生成 xlsx 文档转发（微信/QQ 等）
  const handleShareUser = useCallback(async (u) => {
    const rows = (u.numbers || []).map(n => ({ phone: n || '', name: '' }));
    if (!rows.length) return;
    const res = await shareFile({
      filename: `${u.name || u.phone || u.id}-${rows.length}条-${filenameTimestamp()}.xlsx`,
      text: buildXlsx(rows),
      ext: 'xlsx',
    });
    if (res === 'shared') showToast('已生成表格文档，请选择微信/QQ等发送', 'success');
    else if (res === 'download') showToast('当前浏览器不支持弹分享面板，已改为直接下载表格文件', 'info');
  }, [showToast]);

  // 代完成：排行榜展开行对"有进行中任务"的账号代为完成任务（管理员/代理通用）
  // 需要先为该用户每个活跃批次补图，走独立弹层 ForceCompleteModal
  const handleForceComplete = useCallback((u) => {
    setForceModalUser({ id: u.id, name: u.name || u.phone || `用户${u.id}` });
  }, []);

  const fetchPuzzle = useCallback(async () => {
    try { setPuzzle((await api.get('/puzzle/admin/overview')).data); } catch { /* 证券汇总取不到就保留旧数据 */ }
  }, []);
  const fetchBadges = useCallback(async () => {
    try { setWdPending(((await api.get('/withdrawal/requests?status=pending')).data.list || []).length); } catch {}
    try { setVrPending((await api.get('/video-review/admin/pending-count')).data.count || 0); } catch {}
  }, []);

  // today 变了（跨天）也重新拉一次：两边“今日成功/今日待办”不能只靠业务事件触发，
  // 不然半夜没人交单时，页面开着也会一直停在昨天的数字。
  useEffect(() => { fetchStats(); fetchPuzzle(); fetchBadges(); }, [fetchStats, fetchPuzzle, fetchBadges, today]);

  // Socket 实时推送：收到 stats_refresh 立即刷新
  useEffect(() => {
    if (!subscribe) return;
    const unsub = subscribe((event) => {
      if (event === 'stats_refresh' || event === 'users_updated' || event === 'settlement_updated') fetchStats();
      if (['puzzle_updated', 'puzzle_session_updated', 'puzzle_pool_updated', 'puzzle_settlement_updated'].includes(event)) fetchPuzzle();
      if (event === 'video_review_submitted' || event === 'withdrawal_updated' || event === 'data_refresh') fetchBadges();
    });
    return unsub;
  }, [subscribe, fetchStats, fetchPuzzle, fetchBadges]);

  // 今日待办：两个业务合在一起，只列“需要处理”的事；没有就显示一句“没有待处理事项”
  const todos = [];
  if (stats && stats.pool.available < 100) todos.push({ key: 'legacy-pool', tone: 'danger', text: stats.pool.available === 0 ? '排五数据已领完' : `排五余量不足 ${stats.pool.available}`, to: isAdmin ? '/admin/import' : null });
  if (puzzle && puzzle.pool_remaining === 0) todos.push({ key: 'puzzle-pool', tone: 'danger', text: '证券数据包已领完', to: isAdmin ? '/admin/puzzle-packages' : null });
  if (puzzle && puzzle.pending_review > 0) todos.push({ key: 'puzzle-review', tone: 'warning', text: `证券待核实 ${puzzle.pending_review}`, to: '/admin/puzzle-records' });
  if (wdPending > 0) todos.push({ key: 'wd', tone: 'warning', text: `提现审批 ${wdPending}`, to: '/admin/me?open=withdrawal' });
  if (vrPending > 0) todos.push({ key: 'vr', tone: 'warning', text: `二次审查 ${vrPending}`, to: '/admin/video-review' });
  // 通用操作：两个业务都用得到的入口（交单报表和号码溯源页内有排五 / 证券切换或同时查两个业务，获客素材、二次审查、通知系统同理）
  const CommonActions = () => (
    <div className="card">
      <div className="section-title" style={{ paddingTop: 4 }}>通用</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 8 }}>
        <ActionBtn icon={<TrendingUp size={18}/>} label="交单报表" onClick={() => navigate('/admin/submission-rate')} />
        <ActionBtn icon={<Search size={18}/>} label="号码溯源" onClick={() => navigate('/admin/phone-query')} />
        <ActionBtn icon={<QrCode size={18}/>} label="获客素材" onClick={() => navigate(isAdmin ? '/admin/qr-manage' : '/qrcode')} />
        <ActionBtn icon={<Video size={18}/>} label="二次审查" badge={vrPending} onClick={() => navigate('/admin/video-review')} />
        {canSendPush && <ActionBtn icon={<Bell size={18}/>} label="通知系统" onClick={() => navigate('/admin/push')} />}
      </div>
    </div>
  );
  const TONE = {
    danger:  { background: 'rgba(255,59,48,0.10)', color: 'var(--c-danger)' },
    warning: { background: 'rgba(255,149,0,0.12)', color: '#b26a00' },
  };

  return (
    <div className="page">
      <div className="header">
        <span>管理后台</span>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <AdminNotificationBell />
          <button className="btn btn-sm btn-outline" onClick={() => navigate('/')} style={{ fontSize: 12 }}>前台</button>
        </div>
      </div>

      <div style={{ padding: '12px 16px' }}>
        <div className="card gradient-purple" style={{ color: 'white', padding: 20 }}>
          <div style={{ fontSize: 13, opacity: 0.8, fontWeight: 500 }}>欢迎回来</div>
          <div style={{ fontSize: 22, fontWeight: 800, marginTop: 4 }}>{user?.name || '管理员'}</div>
          <div style={{ fontSize: 12, opacity: 0.6, marginTop: 4 }}>{isAdmin ? '系统管理员' : '代理商'}</div>
        </div>


        {/* 今日待办：两个业务合在一起 */}
        <div className="card" style={{ padding: '12px 14px' }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--c-text-secondary)', marginBottom: 8 }}>今日待办</div>
          {todos.length === 0 ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--c-text-tertiary)' }}>
              <CheckCircle2 size={15} color="var(--c-success)" /> 没有待处理事项
            </div>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {todos.map(t => (
                <button key={t.key} onClick={() => t.to && navigate(t.to)}
                  style={{ ...TONE[t.tone], border: 'none', borderRadius: 999, padding: '6px 12px', fontSize: 13, fontWeight: 600, cursor: t.to ? 'pointer' : 'default', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                  {t.tone === 'danger' && <AlertTriangle size={13} />}{t.text}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* 我的收益（代理商）：排五 + 证券合计，不随业务切换而变，所以放在切换上面 */}
        {isAgent && agentOverview && (
          <div className="card gradient-blue" style={{ color: 'white', padding: 18 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, opacity: 0.85, fontSize: 13, fontWeight: 500 }}><Wallet size={14} /> 我的收益</div>
            <div style={{ display: 'flex', gap: 16, marginTop: 10 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 24, fontWeight: 800 }}>¥{agentOverview.today.total.toFixed(2)}</div>
                <div style={{ fontSize: 11, opacity: 0.7, marginTop: 2 }}>今日收益</div>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 24, fontWeight: 800 }}>¥{agentOverview.all.total.toFixed(2)}</div>
                <div style={{ fontSize: 11, opacity: 0.7, marginTop: 2 }}>累计收益</div>
              </div>
            </div>
            {/* 收益 = 我能留下的钱 = 下级差价 + 外部接单人差价。管理员付给我的那一笔里有要转付出去的钱，不算收益，只显示“已收到” */}
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <div style={{ flex: 1, background: 'rgba(255,255,255,0.15)', borderRadius: 8, padding: '8px 10px', textAlign: 'center' }}>
                <div style={{ fontSize: 14, fontWeight: 700 }}>¥{agentOverview.today.commission.toFixed(2)}</div>
                <div style={{ fontSize: 10, opacity: 0.7 }}>今日下级差价</div>
              </div>
              <div style={{ flex: 1, background: 'rgba(255,255,255,0.15)', borderRadius: 8, padding: '8px 10px', textAlign: 'center' }}>
                <div style={{ fontSize: 14, fontWeight: 700 }}>¥{(agentOverview.today.externalIncome || 0).toFixed(2)}</div>
                <div style={{ fontSize: 10, opacity: 0.7 }}>今日外部接单人</div>
              </div>
            </div>
            {(agentOverview.today.rewards || 0) > 0 && (
              <div style={{ marginTop: 10, fontSize: 11, opacity: 0.85, lineHeight: 1.6 }}>
                {`管理员今日付给我 ¥${agentOverview.today.rewards.toFixed(2)}，其中要转付给下级和外部接单人的钱不算收益`}
              </div>
            )}
            {agentOverview.externalUnverified && agentOverview.externalUnverified.count > 0 && (
              <div style={{ marginTop: 6, fontSize: 11, lineHeight: 1.6, background: 'rgba(255,255,255,0.18)', borderRadius: 8, padding: '6px 10px' }}>
                {`外部接单人${agentOverview.externalUnverified.items.map(x => `「${x.name}」¥${x.amount.toFixed(2)}`).join('、')}${agentOverview.externalUnverified.count > agentOverview.externalUnverified.items.length ? ' 等' : ''}的分账无法核实成本（没有可对照的基准价），暂按全额计入收益`}
              </div>
            )}
          </div>
        )}

        {/* 排五 / 证券 业务切换（与前台、结算、获客素材共用同一个选择） */}
        <div style={{ marginBottom: 12 }}><BizSwitch /></div>

        {bizMode === 'legacy' ? (
          <>
                    {stats && (
              <>
              <div className="card" style={{ display: 'flex' }}>
              <div className="stat-card" style={{ flex: 1, minWidth: 0 }}>
                {stats.pool.available < 100
                  ? <AlertTriangle size={18} color="var(--c-danger)" style={{ margin: '0 auto 4px' }} />
                  : <Database size={18} color="var(--c-primary)" style={{ margin: '0 auto 4px' }} />
                }
                <div className="stat-value" style={{ color: stats.pool.available < 100 ? 'var(--c-danger)' : 'var(--c-primary)' }}>
                  {stats.pool.available}
                </div>
                <div className="stat-label" style={{ color: stats.pool.available < 100 ? 'var(--c-danger)' : undefined }}>
                  {stats.pool.available === 0 ? '已领完' : stats.pool.available < 100 ? '余量不足' : '池中剩余'}
                </div>
              </div>
              <div style={{ width: 0.5, background: 'var(--c-divider)', margin: '10px 0' }} />
              <div className="stat-card" style={{ flex: 1, minWidth: 0 }}>
                <Activity size={18} color="var(--c-success)" style={{ margin: '0 auto 4px' }} />
                <div className="stat-value" style={{ color: 'var(--c-success)' }}>{stats.today.totalDispatched}</div>
                <div className="stat-label">{isAgent ? '团队今日已派' : '今日已派'}</div>
              </div>
              <div style={{ width: 0.5, background: 'var(--c-divider)', margin: '10px 0' }} />
              <div className="stat-card" style={{ flex: 1, minWidth: 0 }}>
                <CheckCircle2 size={18} color="var(--c-primary)" style={{ margin: '0 auto 4px' }} />
                <div className="stat-value" style={{ color: 'var(--c-primary)' }}>{stats.today.successCount ?? 0}</div>
                <div className="stat-label">{isAgent ? '团队成功数量' : '成功数量'}</div>
              </div>
            </div>


                <div className="card">
                  <div className="section-title" style={{ paddingTop: 4 }}>排五操作</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 8 }}>
                    {isAdmin && <ActionBtn icon={<Download size={18}/>} label="导入数据" onClick={() => navigate('/admin/import')} />}
                    {/* 代提（派发数据）和代传（上传图片）在同一个页面里，页内有切换；和证券的“代提代传”一致 */}
                    <ActionBtn icon={<ClipboardList size={18}/>} label="代提代传" onClick={() => navigate('/admin/proxy')} />
                    <ActionBtn icon={<ScanLine size={18}/>} label="识别记录" onClick={() => navigate('/admin/ocr')} />
                    <ActionBtn icon={<Camera size={18}/>} label="图片管理" onClick={() => navigate('/admin/images')} />
                  </div>
                </div>

                <CommonActions />

{stats.userRanking?.length > 0 ? (() => {
                  const RANKING_PAGE_SIZE = 20;
                  const totalPages = Math.max(1, Math.ceil(stats.userRanking.length / RANKING_PAGE_SIZE));
                  const safePage = Math.min(rankingPage, totalPages - 1);
                  const pageStart = safePage * RANKING_PAGE_SIZE;
                  const pagedRanking = stats.userRanking.slice(pageStart, pageStart + RANKING_PAGE_SIZE);
                  return (
                  <div className="card">
                    <div className="section-title" style={{ paddingTop: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span>今日接量排行</span>
                      {totalPages > 1 && (
                        <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--c-text-secondary)' }}>
                          共 {stats.userRanking.length} 人 · 第 {safePage + 1}/{totalPages} 页
                        </span>
                      )}
                    </div>
                    {pagedRanking.map((u, pi) => {
                      const i = pageStart + pi;
                      return (
                      <div key={u.id}>
                        <div className="data-row" style={{ cursor: 'pointer' }}
                          onClick={() => setExpandedId(expandedId === u.id ? null : u.id)}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <span style={{ width: 24, height: 24, borderRadius: 8, background: i < 3 ? 'var(--c-primary)' : 'var(--c-bg)', color: i < 3 ? 'white' : 'var(--c-text-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700 }}>{i + 1}</span>
                            <div>
                              <div style={{ fontWeight: 600, fontSize: 14 }}>
                                {u.name || u.phone}
                                {u.is_external === 1 && <span className="badge" style={{ marginLeft: 6, fontSize: 10, background: 'rgba(255,149,0,0.12)', color: 'var(--c-accent)' }}>外部</span>}
                                {u.role === 'agent' && <span className="badge badge-info" style={{ marginLeft: 6, fontSize: 10 }}>代理</span>}
                                {u.role === 'admin' && <span className="badge" style={{ marginLeft: 6, fontSize: 10, background: 'rgba(0,122,255,0.12)', color: 'var(--c-primary)' }}>管理员</span>}
                              </div>
                              <div style={{ fontSize: 12, color: 'var(--c-text-secondary)' }}>{u.dispatched} 条</div>
                            </div>
                          </div>
                          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span className={`badge ${u.work_status === '已完成' ? 'badge-success' : 'badge-warning'}`}>{u.work_status}</span>
                            {expandedId === u.id ? <ChevronUp size={16} style={{ color: 'var(--c-text-secondary)' }} /> : <ChevronDown size={16} style={{ color: 'var(--c-text-secondary)' }} />}
                          </span>
                        </div>
                        {expandedId === u.id && (
                          <div style={{ padding: '4px 14px 12px 48px' }}>
                            {/* 有进行中任务时提供「代完成」操作（管理员/代理商通用） */}
                            {u.active_count > 0 && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, padding: '8px 10px', background: 'rgba(255,149,0,0.06)', borderRadius: 8 }}>
                                <span style={{ flex: 1, fontSize: 12, color: 'var(--c-text-secondary)' }}>
                                  该账号有 {u.active_count} 个进行中任务批次
                                </span>
                                <button className="btn btn-accent btn-sm" style={{ fontSize: 11, padding: '4px 10px' }}
                                  onClick={(e) => { e.stopPropagation(); handleForceComplete(u); }}>
                                  <CheckCircle2 size={12} style={{ verticalAlign: '-1px', marginRight: 3 }} />代完成
                                </button>
                              </div>
                            )}
                            {(u.numbers || []).length === 0 ? (
                              <div style={{ fontSize: 12, color: 'var(--c-text-tertiary)', padding: '6px 0' }}>暂无号码数据</div>
                            ) : (
                              <>
                                <div style={{ maxHeight: 160, overflowY: 'auto', border: '0.5px solid var(--c-border)', borderRadius: 8, marginBottom: 8 }}>
                                  {(u.numbers || []).map((n, ni) => (
                                    <div key={ni} style={{
                                      display: 'flex', alignItems: 'center', gap: 10,
                                      padding: '7px 10px', borderBottom: ni < (u.numbers || []).length - 1 ? '0.5px solid var(--c-border)' : 'none'
                                    }}>
                                      <span style={{ fontFamily: 'monospace', fontSize: 14, fontWeight: 600, color: 'var(--c-primary)', flex: 1, letterSpacing: '0.5px' }}>{n}</span>
                                      <button className="btn btn-outline btn-sm" style={{ fontSize: 11, padding: '3px 8px' }}
                                        onClick={(e) => { e.stopPropagation(); copyText(n, `已复制 ${n}`); }}>
                                        <Copy size={12} /> 复制
                                      </button>
                                    </div>
                                  ))}
                                </div>
                                <div style={{ display: 'flex', gap: 8 }}>
                                  <button onClick={(e) => { e.stopPropagation(); handleCopyUserAll(u); }}
                                    style={{
                                      flex: 1, padding: '8px 0', borderRadius: 8, cursor: 'pointer',
                                      background: 'var(--c-primary-light)', border: '1px solid rgba(0,122,255,0.2)',
                                      fontSize: 13, fontWeight: 600, color: 'var(--c-primary)',
                                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6
                                    }}>
                                    {copiedAllId === u.id ? <><Check size={14} /> 已复制全部 {u.numbers.length} 个</> : <><PhoneCall size={14} /> 复制全部号码</>}
                                  </button>
                                  <button onClick={(e) => { e.stopPropagation(); handleShareUser(u); }}
                                    style={{
                                      flex: 1, padding: '8px 0', borderRadius: 8, cursor: 'pointer',
                                      background: 'var(--c-success)', border: 'none', color: '#fff',
                                      fontSize: 13, fontWeight: 600,
                                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6
                                    }}>
                                    <FileSpreadsheet size={14} /> 转发文档
                                  </button>
                                </div>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                      );
                    })}
                    {totalPages > 1 && (
                      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, marginTop: 10, paddingTop: 10, borderTop: '0.5px solid var(--c-border)' }}>
                        <button className="btn btn-outline btn-sm" disabled={safePage === 0}
                          onClick={() => setRankingPage(p => Math.max(0, p - 1))}
                          style={{ opacity: safePage === 0 ? 0.4 : 1 }}>
                          上一页
                        </button>
                        <span style={{ fontSize: 12, color: 'var(--c-text-secondary)' }}>{safePage + 1} / {totalPages}</span>
                        <button className="btn btn-outline btn-sm" disabled={safePage >= totalPages - 1}
                          onClick={() => setRankingPage(p => Math.min(totalPages - 1, p + 1))}
                          style={{ opacity: safePage >= totalPages - 1 ? 0.4 : 1 }}>
                          下一页
                        </button>
                      </div>
                    )}
                  </div>
                  );
                })() : (
                  <div className="card">
                    <div className="section-title" style={{ paddingTop: 4 }}>今日接量排行</div>
                    <div style={{ padding: '14px 0', textAlign: 'center', fontSize: 13, color: 'var(--c-text-tertiary)' }}>今天还没有人接量</div>
                  </div>
                )}
              </>
            )}
          </>
        ) : (
          <>
            {puzzle ? (
              <>
                <div className="card" style={{ display: 'flex' }}>
                  <div className="stat-card" style={{ flex: 1, minWidth: 0 }}>
                    {puzzle.pool_remaining === 0
                      ? <AlertTriangle size={18} color="var(--c-danger)" style={{ margin: '0 auto 4px' }} />
                      : <Package size={18} color="var(--c-primary)" style={{ margin: '0 auto 4px' }} />}
                    <div className="stat-value" style={{ color: puzzle.pool_remaining === 0 ? 'var(--c-danger)' : 'var(--c-primary)' }}>{puzzle.pool_remaining}</div>
                    <div className="stat-label" style={{ color: puzzle.pool_remaining === 0 ? 'var(--c-danger)' : undefined }}>{puzzle.pool_remaining === 0 ? '已领完' : '池中剩余包'}</div>
                  </div>
                  <div style={{ width: 0.5, background: 'var(--c-divider)', margin: '10px 0' }} />
                  <div className="stat-card" style={{ flex: 1, minWidth: 0 }}>
                    <Activity size={18} color="var(--c-success)" style={{ margin: '0 auto 4px' }} />
                    <div className="stat-value" style={{ color: 'var(--c-success)' }}>{puzzle.today_success}</div>
                    <div className="stat-label">{isAgent ? '团队成功数量' : '成功数量'}</div>
                  </div>
                  <div style={{ width: 0.5, background: 'var(--c-divider)', margin: '10px 0' }} />
                  <div className="stat-card" style={{ flex: 1, minWidth: 0, cursor: 'pointer' }} onClick={() => navigate('/admin/puzzle-records')}>
                    <ShieldAlert size={18} color={puzzle.pending_review > 0 ? 'var(--c-accent)' : 'var(--c-text-tertiary)'} style={{ margin: '0 auto 4px' }} />
                    <div className="stat-value" style={{ color: puzzle.pending_review > 0 ? 'var(--c-accent)' : 'var(--c-text-secondary)' }}>{puzzle.pending_review}</div>
                    <div className="stat-label">待核实</div>
                  </div>
                </div>

                <div className="card">
                  <div className="section-title" style={{ paddingTop: 4 }}>证券操作</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 8 }}>
                    {isAdmin && <ActionBtn icon={<Download size={18}/>} label="导入数据" onClick={() => navigate('/admin/puzzle-packages')} />}
                    {/* 代提（替人领包）和代传（替人上传三图）在同一个页面里，页内有切换 */}
                    <ActionBtn icon={<PackageCheck size={18}/>} label="代提代传" onClick={() => navigate('/admin/puzzle-proxy')} />
                    {/* 识别记录（待核实等，和排五的识别记录对应）+ 图片管理（看图、批量导出、清理磁盘）：同一个页面的两个标签，这里各给一个直达入口；待核实数量挂在识别记录上 */}
                    <ActionBtn icon={<ShieldAlert size={18}/>} label="识别记录" badge={puzzle.pending_review} onClick={() => navigate('/admin/puzzle-records')} />
                    <ActionBtn icon={<Camera size={18}/>} label="图片管理" onClick={() => navigate('/admin/puzzle-records?tab=images')} />
                  </div>
                </div>

                <CommonActions />

                <div className="card">
                  <div className="section-title" style={{ paddingTop: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span>今日成功排行</span>
                    <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--c-text-secondary)' }}>持有 {puzzle.active_packages} 个包 · {puzzle.holders} 人</span>
                  </div>
                  {puzzle.ranking.length === 0 ? (
                    <div style={{ padding: '14px 0', textAlign: 'center', fontSize: 13, color: 'var(--c-text-tertiary)' }}>今天还没有人提交成功单</div>
                  ) : puzzle.ranking.map((u, i) => (
                    <div key={u.id} className="data-row">
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                        <span style={{ width: 24, height: 24, borderRadius: 8, background: i < 3 && u.today_success > 0 ? 'var(--c-primary)' : 'var(--c-bg)', color: i < 3 && u.today_success > 0 ? 'white' : 'var(--c-text-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, flexShrink: 0 }}>{i + 1}</span>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontWeight: 600, fontSize: 14, wordBreak: 'break-all' }}>
                            {u.name}
                            {u.is_external && <span className="badge" style={{ marginLeft: 6, fontSize: 10, background: 'rgba(255,149,0,0.12)', color: 'var(--c-accent)' }}>外部</span>}
                            {u.role === 'agent' && <span className="badge badge-info" style={{ marginLeft: 6, fontSize: 10 }}>代理</span>}
                            {u.role === 'admin' && <span className="badge" style={{ marginLeft: 6, fontSize: 10, background: 'rgba(0,122,255,0.12)', color: 'var(--c-primary)' }}>管理员</span>}
                          </div>
                          <div style={{ fontSize: 12, color: 'var(--c-text-secondary)' }}>{u.today_success} 单{u.pending > 0 ? ` · 待核实 ${u.pending}` : ''}</div>
                        </div>
                      </div>
                      <span className={`badge ${u.active_packages > 0 ? 'badge-warning' : 'badge-success'}`} style={{ flexShrink: 0 }}>
                        {u.active_packages > 0 ? `持有 ${u.active_packages} 个包` : '已完成'}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            ) : (!loading && <p style={{ textAlign: 'center', padding: 30, color: 'var(--c-text-secondary)' }}>证券数据加载中...</p>)}
          </>
        )}
        {loading && <p style={{ textAlign: 'center', padding: 40, color: 'var(--c-text-secondary)' }}>加载中...</p>}
      </div>

      {forceModalUser && (
        <ForceCompleteModal
          userId={forceModalUser.id}
          userName={forceModalUser.name}
          onClose={() => setForceModalUser(null)}
          onDone={() => { setForceModalUser(null); showToast('已代为完成', 'success'); fetchStats(); }}
        />
      )}

      <TabBar mode="admin" />
    </div>
  );
}

function ActionBtn({ icon, label, onClick, badge }) {
  return (
    <button className="btn btn-outline" onClick={onClick} style={{ justifyContent: 'flex-start', gap: 8, padding: '12px 14px', fontSize: 14, position: 'relative' }}>
      {icon} {label}
      {badge > 0 && (
        <span style={{ marginLeft: 'auto', background: 'var(--c-danger)', color: '#fff', borderRadius: 10, fontSize: 11, fontWeight: 700, padding: '1px 7px', minWidth: 18, textAlign: 'center' }}>{badge}</span>
      )}
    </button>
  );
}
